import { numberToCurrency } from "helpers/string"
import { computeDiscountAmount } from "utils/discountUtils"
import { grossUpCardFee } from "utils/bookingCalculations"
import { summarizeAddOnFees } from "components/bookings/helpers"

const WEEKDAY_KEYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"]

const dayAdjustmentApplies = (adjustment, weekdayKey) => {
  const { applies_on, custom_days } = adjustment || {}
  if (applies_on === "weekend") return weekdayKey === "sat" || weekdayKey === "sun"
  if (applies_on === "weekdays") return weekdayKey !== "sat" && weekdayKey !== "sun"
  if (applies_on === "custom") return (custom_days || []).includes(weekdayKey)
  return false
}

/**
 * Returns the day adjustments (from experience.day_adjustments) that apply to the trip's
 * start date, based on that date's weekday vs. each adjustment's applies_on/custom_days.
 * Adjustments scoped to specific ticket types (affects: "selected") are skipped — private
 * bookings have no ticket-type selection.
 */
export const getApplicableDayAdjustments = (dayAdjustments, dateStr) => {
  if (!Array.isArray(dayAdjustments) || !dayAdjustments.length || !dateStr) return []
  const [year, month, day] = dateStr.split("-").map(Number)
  const weekdayKey = WEEKDAY_KEYS[new Date(year, month - 1, day).getDay()]
  return dayAdjustments.filter((adjustment) => adjustment.affects !== "selected" && dayAdjustmentApplies(adjustment, weekdayKey))
}

// Dates arrive as "YYYY-MM-DD" or a full ISO timestamp; only the calendar day matters here.
const toIsoDate = (value) => (value ? String(value).slice(0, 10) : "")
const toMonthDay = (isoDate) => Number(isoDate.slice(5, 7)) * 100 + Number(isoDate.slice(8, 10))

/**
 * Whether a booking date falls inside one adjustment range. With is_repeat_each_year the stored
 * year is ignored and only month/day is compared, so a range that wraps the year end
 * (e.g. Dec 20 – Jan 5) matches on both sides of the boundary. Otherwise the literal dates are
 * compared (ISO date strings sort lexicographically, so a plain string compare is correct).
 */
const dateInAdjustmentRange = (dateStr, range, isRepeatEachYear) => {
  const start = toIsoDate(range?.start)
  const end = toIsoDate(range?.end)
  if (!start || !end) return false
  if (!isRepeatEachYear) return dateStr >= start && dateStr <= end
  const [target, from, to] = [toMonthDay(dateStr), toMonthDay(start), toMonthDay(end)]
  return from <= to ? target >= from && target <= to : target >= from || target <= to
}

/**
 * Resolves the date ranges a seasonal adjustment covers, normalized to { start, end }:
 *  - default_schedule → the experience's own season ranges (trip.available_date_ranges)
 *  - custom_dates     → the adjustment's nested date_ranges
 *  - ticketed         → the flat start_date / end_date pair
 * "holidays" adjustments carry no dates the frontend can resolve, so they never match here —
 * the backend owns that calendar.
 */
const getSeasonalAdjustmentRanges = (adjustment, seasonRanges) => {
  if (adjustment.applies_on_schedule_type === "default_schedule") return seasonRanges
  if (adjustment.date_ranges?.length) {
    return adjustment.date_ranges.map((range) => ({ start: range.start_date, end: range.end_date }))
  }
  if (adjustment.start_date && adjustment.end_date) {
    return [{ start: adjustment.start_date, end: adjustment.end_date }]
  }
  return []
}

/**
 * Returns the seasonal adjustments (from experience.seasonal_adjustments) covering the trip's
 * start date. Adjustments scoped to specific ticket types (affects: "selected") are skipped —
 * private bookings have no ticket-type selection.
 * @param {Array} seasonalAdjustments - experience.seasonal_adjustments
 * @param {String} dateStr - trip start date ("YYYY-MM-DD")
 * @param {Array} seasonRanges - experience.available_date_ranges ([{ start, end }]), used by
 *   adjustments that apply on the experience's default schedule.
 */
export const getApplicableSeasonalAdjustments = (seasonalAdjustments, dateStr, seasonRanges = []) => {
  if (!Array.isArray(seasonalAdjustments) || !seasonalAdjustments.length || !dateStr) return []
  const bookingDate = toIsoDate(dateStr)
  return seasonalAdjustments.filter((adjustment) => {
    if (adjustment.affects === "selected") return false
    const isRepeatEachYear = adjustment.is_repeat_each_year ?? true
    return getSeasonalAdjustmentRanges(adjustment, seasonRanges ?? []).some((range) =>
      dateInAdjustmentRange(bookingDate, range, isRepeatEachYear)
    )
  })
}

/**
 * Layered pricing: every adjustment that applies to the date is computed off the BASE unit price
 * and the deltas are summed — a $50 base with a +20% weekend and a +20% seasonal adjustment is
 * $70, not $72. Fixed "decrease" adjustments can exceed the base, so the result is floored at 0.
 * @param {Number|String} price - base unit price
 * @param {Array} adjustments - applicable day + seasonal adjustments
 */
const getAdjustmentsDelta = (base, adjustments = []) =>
  adjustments.reduce((sum, adjustment) => {
    const rawAmount =
      adjustment.price_type === "percent"
        ? (base * (Number(adjustment.price_value) || 0)) / 100
        : Number(adjustment.price_value) || 0
    return sum + (adjustment.direction === "decrease" ? -rawAmount : rawAmount)
  }, 0)

export const applyPriceAdjustments = (price, adjustments = []) => {
  const base = Number.parseFloat(price ?? 0) || 0
  return Math.max(0, base + getAdjustmentsDelta(base, adjustments))
}

/**
 * True when the decreases stacked on a non-zero base price wipe it out entirely, so the displayed
 * $0.00 is the floor rather than the real arithmetic. Drives the explanatory tooltip on the
 * affected breakdown line — the price itself stays clamped either way.
 * @param {Number|String} price - base unit price
 * @param {Array} adjustments - applicable day + seasonal adjustments
 */
export const isPriceZeroedByAdjustments = (price, adjustments = []) => {
  const base = Number.parseFloat(price ?? 0) || 0
  if (base <= 0 || adjustments.length === 0) return false
  return base + getAdjustmentsDelta(base, adjustments) <= 0
}

/**
 * Computes the guest-visible portion of the platform tax based on platform_fee_setting.
 * guide_pays  → guest pays $0
 * guest_pays  → guest pays full amount
 * split+%     → guest pays (100 - guide_portion)% of the tax
 * split+fixed → guest pays max(0, tax - guide_portion)
 */
export const applyPlatformFeeSetting = (rawTaxAmount, platformFeeSetting) => {
  if (!platformFeeSetting) return rawTaxAmount
  const { fee_payer_mode, split_type, guide_portion } = platformFeeSetting
  if (fee_payer_mode === "guide_pays") return 0
  if (fee_payer_mode === "guest_pays") return rawTaxAmount
  if (fee_payer_mode === "split") {
    const guideShare = Number(guide_portion) || 0
    if (split_type === "fixed") return Math.max(0, rawTaxAmount - guideShare)
    // percentage
    return rawTaxAmount * (1 - guideShare / 100)
  }
  return rawTaxAmount
}

/**
 * Computes the GROSS guest subtotal (before discount, add-ons, and tax) from a saved booking's
 * payment_summary. The per-guest entries (adult/child/…) store `price` as the LINE total
 * (count × unit), so we sum those line totals. Used to seed bookingSelections.baseAmount in edit
 * mode — payment_summary.subtotal is net of the discount, so it can't be used as the discount base.
 * @param {Object} paymentSummary - booking.payment_summary
 */
export const computeGuestSubtotalFromPaymentSummary = (paymentSummary = {}) => {
  const {
    adult, child, infant, passenger,
    pricing_type, extra_adult, extra_child, extra_infant, extra_passenger, flat_rate
  } = paymentSummary ?? {}
  const lineTotal = (item) => (item?.count ? Number(item?.price) || 0 : 0)
  if (pricing_type === "charge_flat_rate") {
    return (
      (Number(flat_rate) || 0) +
      lineTotal(extra_adult) + lineTotal(extra_child) + lineTotal(extra_infant) + lineTotal(extra_passenger)
    )
  }
  return lineTotal(adult) + lineTotal(child) + lineTotal(infant) + lineTotal(passenger)
}

/**
 * Builds a price breakdown array (guest line items + grouped "Tax & Fees") from a saved
 * booking's payment_summary. Used to seed bookingSelections.breakdown in edit mode so the
 * "Updated Price Details" panel shows the full breakdown before the user changes anything
 * (the live recalc via calculateBookingTotal is skipped while there are no changes).
 *
 * The output format mirrors calculateBookingTotal's breakdown so PriceBreakdown renders it
 * identically. Discount and add-ons are intentionally excluded — PriceBreakdown renders those
 * as separate rows.
 * @param {Object} paymentSummary - booking.payment_summary
 */
export const buildBreakdownFromPaymentSummary = (paymentSummary = {}) => {
  const {
    tax_and_fee,
    adult,
    child,
    infant,
    passenger,
    pricing_type,
    extra_adult,
    extra_child,
    extra_infant,
    extra_passenger,
    flat_rate,
    custom_fees
  } = paymentSummary ?? {}

  const breakdown = []

  const addGuestLine = (label, item) => {
    if (item?.count && item?.price) {
      breakdown.push({ label: `${label} x ${item.count}`, amount: numberToCurrency(Number(item.price)) })
    }
  }

  if (pricing_type === "charge_flat_rate") {
    breakdown.push({ label: "Flat Rate", amount: numberToCurrency(Number(flat_rate ?? 0)) })
    addGuestLine("Extra Adult", extra_adult)
    addGuestLine("Extra Child", extra_child)
    addGuestLine("Extra Infant", extra_infant)
    addGuestLine("Extra Passenger", extra_passenger)
  } else {
    addGuestLine("Adult", adult)
    addGuestLine("Child", child)
    addGuestLine("Infant", infant)
    addGuestLine("Passenger", passenger)
  }

  const feeChildren = (custom_fees ?? []).map((fee) => ({
    label: fee.title,
    amount: numberToCurrency(Number(fee.price))
  }))
  // The platform fee (tax_and_fee) is already represented inside custom_fees as a "Platform Fee"
  // entry. Only add it separately for legacy bookings whose custom_fees omit it — otherwise it
  // renders twice.
  const hasPlatformFee = (custom_fees ?? []).some((fee) => fee.title === "Platform Fee")
  const platformFeeAmount = hasPlatformFee ? 0 : Number(tax_and_fee ?? 0)
  if (platformFeeAmount > 0) {
    feeChildren.push({ label: "Platform Fee", amount: numberToCurrency(platformFeeAmount) })
  }
  const customFeeTotal = (custom_fees ?? []).reduce((sum, fee) => sum + Number(fee.price), 0)
  const taxFeesTotal = customFeeTotal + platformFeeAmount
  if (taxFeesTotal > 0) {
    breakdown.push({
      label: "Tax & Fees",
      amount: numberToCurrency(taxFeesTotal),
      children: feeChildren.length > 0 ? feeChildren : undefined
    })
  }

  // Post-creation manual price adjustment (booking-details "Adjust Booking Price") — carried as a
  // flat line after Tax & Fees so the seeded breakdown reconciles with total_price (which includes it).
  const priceAdjustmentTotal = (paymentSummary?.adjust_price_history ?? []).reduce(
    (sum, item) => sum + Number(item?.price_difference || 0),
    0
  )
  if (priceAdjustmentTotal) {
    breakdown.push({
      label: "Price Change",
      amount: `${priceAdjustmentTotal > 0 ? "+" : ""}${numberToCurrency(priceAdjustmentTotal)}`,
      isPriceChange: true
    })
  }

  return breakdown
}

/**
 * Calculates the total booking amount and breakdown
 * @param {Object} bookingSelections - The booking selections object
 * @param {Object} counts - Guest counts
 * @param {Number} guideFeePercent - Guide fee percentage
 * @param {Object} options - Additional options
 * @param {String} options.paymentType - Payment type (cash, card, link, etc)
 * @param {Boolean} options.isEdit - Whether this is an edit operation
 * @param {Number} options.originalTaxAndFee - Original platform fee from booking.payment_summary.tax_and_fee (cash edit mode only)
 * @param {Array} options.customFees - Custom fees from assigned_custom_fees (e.g. [{ id, title, fee_type: "fixed"|"percentage", price }])
 * @param {Object} options.platformFeeSetting - Platform fee setting ({ fee_payer_mode, split_type?, guide_portion?, card_fee_passthrough_enabled? })
 * @param {Object} options.cardFeeInfo - Card fee info from selected payment method ({ rate, fixed, formula })
 * @param {Number} options.originalCardProcessingFee - Original card processing fee from booking.payment_summary.card_processing_fee (cash edit mode only)
 * @param {Array} options.discountPromos - Effective discount promos ({ value, discount_type, apply_to_add_ons }). The discount is applied to the pre-tax base (guest subtotal + add-ons) BEFORE tax & fees are calculated, matching the backend.
 * @param {Array} options.dayAdjustments - Day adjustments from experience.day_adjustments ({ name, direction, price_value, price_type, applies_on, custom_days, affects }). When the trip's start date (bookingSelections.date) falls on a matching day, these are folded directly into the base unit prices (flat_rate, price_per_person, price_per_child, price_per_infant, price_per_passenger) before any totals are computed.
 * @param {Array} options.seasonalAdjustments - Seasonal adjustments from experience.seasonal_adjustments ({ name, direction, price_value, price_type, applies_on_schedule_type, date_ranges, start_date, end_date, is_repeat_each_year, affects }). Folded into the base unit prices exactly like dayAdjustments when the trip's start date falls inside one of their ranges; the two stack, each computed off the base price.
 * @param {Array} options.seasonRanges - The experience's own season windows (experience.available_date_ranges, [{ start, end }]). Supplies the dates for seasonal adjustments whose applies_on_schedule_type is "default_schedule".
 * @param {Number} options.depositFraction - Fraction of the pre-card total paid by card NOW (deposit% / 100 for deposit payments; 1 for full payment). The card processing fee is charged only on this pay-now portion, not on the full total.
 * @param {Array} options.addOnsCatalog - Experience add-ons catalog (experience.add_ons). Used with the selections in bookingSelections.addOns to decide, per add-on, whether the platform fee (add_on_category.commission_applicable / commission_percentage) and the custom percentage fees (apply_custom_tax_and_fees) apply to it. The card processing fee always applies to every add-on regardless of these flags.
 * @param {Number} options.priceAdjustmentTotal - Cumulative post-creation manual price adjustment (sum of payment_summary.adjust_price_history.price_difference). Folded into the tax & fee base so percentage-based fees apply to the adjusted amount (matching the stored "Previous Price Details"), then added as a flat "Price Change" line after Tax & Fees so the details-page "Adjust Booking Price" change persists through edits. Edit mode only (0 for new bookings).
 * @returns {{ total: number, guestSubtotal: number, discountAmount: number, breakdown: Array, preCardTotal: number, cardProcessingFee: number }}
 *   `total` includes the card fee on the deposit; `preCardTotal` is the total before any card fee
 *   (the base the deposit % is taken from); `cardProcessingFee` is the deposit's card fee.
 */
export const calculateBookingTotal = (bookingSelections, counts, guideFeePercent, options = {}) => {
  const {
    paymentType,
    isEdit,
    originalTaxAndFee,
    customFees = [],
    platformFeeSetting,
    cardFeeInfo,
    originalCardProcessingFee,
    discountPromos = [],
    dayAdjustments = [],
    seasonalAdjustments = [],
    seasonRanges = [],
    depositFraction = 1,
    overrideGuestSubtotal,
    addOnsCatalog = [],
    priceAdjustmentTotal = 0
  } = options
  let total = 0
  const breakdown = []
  const { pricing_type, flat_rate, price_per_person, price_per_child, price_per_infant, price_per_passenger, included_guests, max_guests } =
    bookingSelections?.tripLength?.price || {}

  // Fold any matching day adjustments (e.g. weekend surcharges) and seasonal adjustments
  // (e.g. a holiday range) directly into the base unit prices before computing totals, so they
  // apply to the flat rate / per-person rates and to extra-guest per-person rates alike.
  // Both kinds stack: each is computed off the base price and the deltas are summed.
  const applicableAdjustments = [
    ...getApplicableDayAdjustments(dayAdjustments, bookingSelections?.date),
    ...getApplicableSeasonalAdjustments(seasonalAdjustments, bookingSelections?.date, seasonRanges)
  ]
  const adjustPrice = (price) => applyPriceAdjustments(price, applicableAdjustments)
  // Flags the lines whose $0.00 is the clamp rather than the real total, so the breakdown can
  // explain it instead of silently showing a free trip.
  const isZeroed = (price) => isPriceZeroedByAdjustments(price, applicableAdjustments)

  const adjustedFlatRate = adjustPrice(flat_rate)
  const adjustedPricePerPerson = adjustPrice(price_per_person)
  const adjustedPricePerChild = adjustPrice(price_per_child)
  const adjustedPricePerInfant = adjustPrice(price_per_infant)
  const adjustedPricePerPassenger = adjustPrice(price_per_passenger)

  const mainCounts = counts?.main || {}
  const extraCounts = counts?.extra || {}

  // Merge counts for calculation if pricing is flat rate, assuming all inputs flow into main or separate inputs need merging
  // If we unify inputs in GuestInputGroup, mainCounts will hold everything.
  // But we handle both for safety.
  const totalCounts = {
    adults: (mainCounts.adults || 0) + (extraCounts.adults || 0),
    children: (mainCounts.children || 0) + (extraCounts.children || 0),
    infants: (mainCounts.infants || 0) + (extraCounts.infants || 0),
    passengers: (mainCounts.passengers || 0) + (extraCounts.passengers || 0)
  }

  if (pricing_type === "charge_flat_rate") {
    const flatAmount = adjustedFlatRate
    const hasGuests = Object.values(totalCounts).some((val) => val > 0)

    if (hasGuests) {
      total += flatAmount

      const guestsIncluded = included_guests ?? max_guests ?? 0
      let remainingCapacity = guestsIncluded

      // 1. Adults
      const adultsIncluded = Math.min(totalCounts.adults, remainingCapacity)
      remainingCapacity = Math.max(0, remainingCapacity - adultsIncluded)

      // 2. Children
      const childrenIncluded = Math.min(totalCounts.children, remainingCapacity)
      remainingCapacity = Math.max(0, remainingCapacity - childrenIncluded)

      // 3. Infants
      const infantsIncluded = Math.min(totalCounts.infants, remainingCapacity)

      const extraAdults = Math.max(0, totalCounts.adults - adultsIncluded)
      const extraChildren = Math.max(0, totalCounts.children - childrenIncluded)
      const extraInfants = Math.max(0, totalCounts.infants - infantsIncluded)

      breakdown.push({
        label: "Flat Rate",
        amount: numberToCurrency(flatAmount),
        isZeroedByAdjustment: isZeroed(flat_rate),
        pricingDetails: {
          pricing_type: "charge_flat_rate",
          included_guests: guestsIncluded,
          price_per_person: adjustedPricePerPerson,
          price_per_child: adjustedPricePerChild,
          price_per_infant: adjustedPricePerInfant
        }
      })

      if (extraAdults > 0) {
        const lineTotal = extraAdults * adjustedPricePerPerson
        total += lineTotal
        breakdown.push({
          label: `Extra Adult x ${extraAdults}`,
          amount: numberToCurrency(lineTotal),
          isZeroedByAdjustment: isZeroed(price_per_person)
        })
      }
      if (extraChildren > 0) {
        const lineTotal = extraChildren * adjustedPricePerPerson
        total += lineTotal
        breakdown.push({
          label: `Extra Child x ${extraChildren}`,
          amount: numberToCurrency(lineTotal),
          isZeroedByAdjustment: isZeroed(price_per_person)
        })
      }
      if (extraInfants > 0) {
        const lineTotal = extraInfants * adjustedPricePerPerson
        total += lineTotal
        breakdown.push({
          label: `Extra Infant x ${extraInfants}`,
          amount: numberToCurrency(lineTotal),
          isZeroedByAdjustment: isZeroed(price_per_person)
        })
      }
      if (totalCounts.passengers > 0) {
        const lineTotal = totalCounts.passengers * adjustedPricePerPerson
        total += lineTotal
        breakdown.push({
          label: `Extra Passenger x ${totalCounts.passengers}`,
          amount: numberToCurrency(lineTotal),
          isZeroedByAdjustment: isZeroed(price_per_person)
        })
      }
    }
  } else {
    // Per Person Pricing
    const guestMapping = [
      { key: "adults", label: "Adult", basePrice: price_per_person, price: adjustedPricePerPerson },
      { key: "children", label: "Child", basePrice: price_per_child, price: adjustedPricePerChild },
      { key: "infants", label: "Infant", basePrice: price_per_infant, price: adjustedPricePerInfant },
      { key: "passengers", label: "Passenger", basePrice: price_per_passenger, price: adjustedPricePerPassenger }
    ]

    for (const { key, label, basePrice, price } of guestMapping) {
      const count = totalCounts[key] || 0
      if (count > 0) {
        const unitPrice = price
        const lineTotal = unitPrice * count
        total += lineTotal
        breakdown.push({
          label: `${label} x ${count}`,
          amount: numberToCurrency(lineTotal),
          isZeroedByAdjustment: isZeroed(basePrice)
        })
      }
    }
  }

  // Capture the pricing-loop result before any override so callers can display the original.
  const calculatedGuestSubtotal = total

  
  if (overrideGuestSubtotal != null && overrideGuestSubtotal >= 0) {
    total = overrideGuestSubtotal
  }

  // Save guest subtotal before adding fees (used as base for percentage-based fees)
  const guestSubtotal = total

  // Add-on prices are always part of the customer total; only the FEES on them are gated per
  // add-on (platform fee via commission_applicable/percentage, custom % fees via
  // apply_custom_tax_and_fees). The card processing fee always applies to every add-on.
  const addOnsTotal = Number(bookingSelections?.addOnsTotal) || 0
  const { customTaxableAddOnsTotal, commissionAddOns } = summarizeAddOnFees(bookingSelections?.addOns, addOnsCatalog)
  total += addOnsTotal

  // Apply the discount to the pre-tax base BEFORE computing tax & fees, matching the backend.
  // Each promo's apply_to_add_ons flag decides whether add-ons are part of its discountable base.
  const rawDiscount = (discountPromos ?? []).reduce(
    (sum, promo) => sum + computeDiscountAmount(promo, guestSubtotal, addOnsTotal),
    0
  )
  const discountAmount = Math.max(0, Math.min(rawDiscount, guestSubtotal + addOnsTotal))
  total -= discountAmount

  // Because add-on portions can now be taxed at different rates, spread the single discount across
  // every taxable portion with a uniform factor so each fee base is reduced proportionally.
  const grossBase = guestSubtotal + addOnsTotal
  const discountFactor = grossBase > 0 ? (grossBase - discountAmount) / grossBase : 1

  // The post-creation manual price adjustment (details-page "Adjust Booking Price") widens the
  // taxable guest base so percentage-based fees apply to the adjusted amount — matching the stored
  // "Previous Price Details". It's added undiscounted here, and also carried as its own "Price
  // Change" line after Tax & Fees (below). Edit mode only (0 for new bookings).
  const adjustedGuestBase = guestSubtotal * discountFactor + priceAdjustmentTotal

  // Custom fees. Percentage fees apply to the guest subtotal plus only the add-ons that opted in
  // via apply_custom_tax_and_fees; fixed fees stay flat (charged once). Exclude system-injected
  // fees (id: null) such as "Platform Fee" and "Card Processing Fee" — those are handled separately
  // via guideFeePercent and cardFeeInfo to avoid double-counting.
  const systemCardFeeEntry = customFees?.find((fee) => fee.id === null && fee.title === "Card Processing Fee")
  const userCustomFees = customFees?.filter((fee) => fee.id !== null) ?? []
  const customFeeBase = adjustedGuestBase + customTaxableAddOnsTotal * discountFactor
  const customFeeItems = []
  let customFeeTotal = 0
  if (userCustomFees.length > 0) {
    for (const fee of userCustomFees) {
      const feeAmount =
        fee.fee_type === "percentage"
          ? (customFeeBase * Number(fee.price)) / 100
          : Number(fee.price)
      if (feeAmount === 0) continue
      total += feeAmount
      customFeeTotal += feeAmount
      customFeeItems.push({ label: fee.title, amount: numberToCurrency(feeAmount) })
    }
  }

  // Card processing fee only applies for card/link/paypalLink — cash has no card to charge a fee
  // to. The platform (DS commission) fee is NOT payment-method-dependent: the guide owes DS the
  // same commission regardless of how the guest pays, so it must recalculate identically for cash
  // and card alike (see PlatformFeeCalculator: "the guide ALWAYS pays their full ds_commission to
  // DS ... only what the CLIENT is charged changes", which is driven by platform_fee_setting, not
  // payment method). Only the fixed edit-mode-cash preserve below (real, already-charged history)
  // overrides this.
  const isCashPayment = paymentType === "cash"
  const isCardOrLinkPayment = paymentType === "card" || paymentType === "link" || paymentType === "paypalLink"

  // Platform fee (commission): recalculated the same way for card AND cash — EXCEPT a brand-new
  // (not-yet-created) full-cash booking. There's no history to preserve there (fixedPlatformFee
  // below requires isEdit), and the backend's TripBooking#ds_client_portion unconditionally zeroes
  // the CLIENT-facing platform fee for that exact case (CASH_BALANCE_PAYMENT_OPTIONS - the guide
  // absorbs the commission from payout instead, since there's no charge to attach it to). Every
  // other cash case - a deposit's cash-balance leg (still paymentType "card" for the deposit) and
  // cash-edit (preserved via fixedPlatformFee) - is unaffected by this and stays in parity with
  // the card calculation. When it does apply, it's the guide fee on the guest subtotal PLUS the
  // commission on any commission-applicable add-ons (each at its own commission_percentage, or the
  // standard guide fee when null), and the combined amount then follows platform_fee_setting below.
  const rawPlatformTax =
    !isEdit && isCashPayment
      ? 0
      : (adjustedGuestBase * (Number(guideFeePercent) || 0)) / 100 +
        commissionAddOns.reduce((sum, addOn) => {
          const rate = addOn.commissionPercentage != null ? addOn.commissionPercentage : Number(guideFeePercent) || 0
          return sum + (addOn.amount * discountFactor * rate) / 100
        }, 0)
  const fixedPlatformFee = isCashPayment && isEdit && originalTaxAndFee != null
    ? Number(originalTaxAndFee)
    : null

  // Card fee: recalculate for card/link; preserve original for cash-edit; zero otherwise.
  // The card processing fee is only passed on to the guest when the experience's
  // platform_fee_setting opts in via card_fee_passthrough_enabled.
  const cardFeePassthroughEnabled = platformFeeSetting?.card_fee_passthrough_enabled === true
  const isCardFeeActive = !!systemCardFeeEntry && systemCardFeeEntry.is_active !== false
  const cardFeeSource = cardFeeInfo ?? (isCardFeeActive ? { rate: Number(systemCardFeeEntry?.price) / 100, fixed: 0.3 } : null)
  const fixedOriginalCardFee = isCashPayment && isEdit && Number(originalCardProcessingFee) > 0
    ? Number(originalCardProcessingFee)
    : 0
  const shouldApplyCardFee =
    (isCardOrLinkPayment && isCardFeeActive && !!cardFeeSource && cardFeePassthroughEnabled) || fixedOriginalCardFee > 0

  // Card fee is declared in the function scope so preCardTotal can be derived after the fee block.
  let cardProcessingFee = 0
  // Show Tax & Fees line if any fee applies
  if (total > 0 && (rawPlatformTax > 0 || fixedPlatformFee != null || customFeeItems.length > 0 || shouldApplyCardFee)) {
    let platformTaxAmount = 0
    if (fixedPlatformFee != null) {
      platformTaxAmount = fixedPlatformFee
      total += platformTaxAmount
    } else if (rawPlatformTax > 0) {
      platformTaxAmount = applyPlatformFeeSetting(rawPlatformTax, platformFeeSetting)
      total += platformTaxAmount
    }

    const allChildren = [...customFeeItems]
    if (platformTaxAmount > 0) {
      allChildren.push({ label: "Platform Fee", amount: numberToCurrency(platformTaxAmount) })
    }

    if (shouldApplyCardFee) {
      // The card fee is charged on the amount paid by card NOW: the deposit (depositFraction of the
      // pre-card total) for deposit payments, or the whole pre-card total when paying in full
      // (depositFraction = 1). At this point `total` is the pre-card, pre-adjustment total.
      // The post-creation price adjustment is included in the fee base whenever the full amount is
      // paid by card (depositFraction = 1: full payment, or deposit + balance-by-card) — otherwise
      // adding a guest could wrongly LOWER the card fee below the stored booking's, since the tax
      // base already includes the adjustment. When only the deposit is swiped now (deposit +
      // balance-in-cash, depositFraction < 1) the adjustment is paid later in cash, so it's excluded.
      // Gross-up so that after Stripe deducts its fee on (depositBase + fee), we net depositBase.
      const adjustmentPaidByCard = depositFraction >= 1 ? priceAdjustmentTotal : 0
      const depositBase = total * depositFraction + adjustmentPaidByCard
      cardProcessingFee = fixedOriginalCardFee > 0
        ? fixedOriginalCardFee
        : grossUpCardFee(depositBase, cardFeeSource)
      total += cardProcessingFee
      allChildren.push({ label: "Card Processing Fee", amount: numberToCurrency(cardProcessingFee) })
    }

    breakdown.push({
      label: "Tax & Fees",
      amount: numberToCurrency(customFeeTotal + platformTaxAmount + cardProcessingFee),
      children: allChildren.length > 0 ? allChildren : undefined
    })
  }

  // preCardTotal is the grand total before the card fee — the base the deposit % is taken from.
  // Captured before the manual price adjustment so the DEPOSIT base stays unaffected by it (the
  // adjustment flows into the balance, not the deposit). The card fee itself does cover the
  // adjustment when the full amount is paid by card (see the card-fee block above).
  const preCardTotal = total - cardProcessingFee

  // Add the post-creation manual price adjustment (booking-details "Adjust Booking Price") as its
  // own "Price Change" line — the raw base delta. Tax & fees on the adjusted amount were already
  // folded into the fee base above (via adjustedGuestBase), so this line only carries the base change,
  // mirroring the stored "Previous Price Details" layout (taxed adjusted amount + separate change).
  if (priceAdjustmentTotal) {
    total += priceAdjustmentTotal
    breakdown.push({
      label: "Price Change",
      amount: `${priceAdjustmentTotal > 0 ? "+" : ""}${numberToCurrency(priceAdjustmentTotal)}`,
      isPriceChange: true
    })
  }

  return { total, guestSubtotal, calculatedGuestSubtotal, discountAmount, breakdown, preCardTotal, cardProcessingFee }
}
